import { call, post } from "@/services/api";

export type Check = "done" | "partial" | "none_done" | null;
export type Item = {
  id: number; name: string; sku: string | null; product_id: number | null; qty: number;
  image_url: string | null; product_url?: string | null; delivery_comment: string | null; delivery_team_comment: string | null;
  parent_item_id: number | null;
  checks: { preparation: Check; delivery: Check; return_preparation: Check; return: Check };
  estimated_minutes: number | null; estimated_minutes_per_piece: number | null;
  estimate_basis: string | null; product_type_slug: string | null;
  needs_assembly: boolean | null;
  assembled_qty: number; checked_qty: number;
  assembled_at: string | null; checked_at: string | null;
  issues: Issue[];
  // A row of Ludovic's list: one product on one supplier order, one order line per piece.
  kind?: "order_line"; line_ids?: number[];
  order_id?: number; supplier?: string | null; arrival_on?: string | null; arrived?: boolean | null;
  cartons?: number; reference?: string | null; reference_order_number?: string | null;
  // His box references from the carton library, when it knows them.
  box_refs?: { no: string; title: string | null }[];
};
export type Issue = {
  id: number; kind: string; description: string;
  myotaku_contract_item_id: number | null; myotaku_order_line_id?: number | null; reported_by: string[];
  blocks_assembly: boolean; reported_at: string; notified_to: string[];
  // His "Combien sont touchés ?", and when the report went out (to `notified_to`).
  affected_qty?: number | null; notified_at?: string | null;
  // Whether Slack really took the message, and whom it did not reach; null before anything was sent.
  delivery?: { state: "sent" | "pending" | "failed"; sent_at: string | null; failed_to: string[]; error: string | null } | null;
};
export type Pack = {
  id: number; name: string | null; room_name: string | null; mode: string | null;
  // Ludovic's list has two: what is built, and what is only opened and checked.
  section?: "assemble" | "check";
  standard_kit: { matches: boolean; qty: number | null };
  estimated_minutes: number;
  total_qty: number; assembled_qty: number; checked_qty: number;
  items: Item[];
};
export type Segment = { id: number; started_at: string; ended_at: string | null; worker_names: string[] };
export type FurnitureList = "orders" | "delivery";
export type Run = {
  id: number; number: number | null; status: string; furniture_list: FurnitureList;
  // The fitters on the job now (kept while paused); minutes_by_person also has those who left.
  team: string[];
  started_at: string | null; finished_at: string | null;
  estimated_minutes: number | null; wall_minutes: number; worked_minutes: number;
  minutes_by_person: Record<string, number>; packed: boolean;
  // The same two, exact (person-seconds, never rounded): the screen rounds once, as his `times`.
  worked_seconds?: number; seconds_by_person?: Record<string, number>;
  // When the server counted them, and how many people its clock counts from then (the card's own pair).
  as_of?: string; running_people?: number;
  packing_confirmed: boolean; blocked: boolean; recap_notes: string | null;
  // His "récap": sent on Slack to the contract's sales reps, Ludo and Emerson in copy.
  // `recap_sent_via`: "slack" once Slack took every message, "unclear" while queued, "failed", or
  // "none" when the fitter chose not to send it.
  recap_sent_at: string | null; recap_sent_to: string[] | null; recap_sent_via: string | null;
  // The run's own copy of the job, for a sheet the feed no longer returns.
  client_name?: string | null; event_number?: number | null; event_on?: string | null; delivery_on?: string | null;
  // His manager box: something recorded to reset, the last reset, and whether it can be undone.
  recorded?: boolean; reset_at?: string | null; undoable?: boolean;
  segments: Segment[];
};
export type EventSummary = {
  id: number; number: number | null; kind: string | null; type_label: string | null;
  client_name: string | null; contract_id: number | null; starts_at: string | null;
  approved: boolean; sales_approved: boolean; logistics_approved: boolean;
  warehouse: { id: number; name: string } | null;
  run: { id: number; status: string; number: number | null } | null;
  // His reception pill, from the supplier documents read at Réception.
  reception?: "warn" | "ok" | "confirmed" | null;
};
// A sales rep's stock reservation, as his "PRÉPARATION CLIENT" — this app's own, never Myotaku's.
export type ClientPreparation = {
  reservation_id: number; reference: string; title: string; notes: string; starts_at: string; ends_at: string;
  warehouse: { id: number; name: string } | null; pieces: number; client_name: string | null;
};
export type Day = { date: string; in_window: boolean; events: EventSummary[]; preparations: ClientPreparation[] };
export type DaysResponse = {
  window: { earliest: string; latest: string };
  warehouses: { id: number; name: string }[];
  days: Day[];
};
export type CitedOrder = {
  id: number; supplier: string | null; title: string | null; arrival_on: string | null; arrived: boolean;
  // His `docsOf`: the order's documents, the client's first, read live (their links expire).
  documents?: SupplierDocument[];
  // The order's documents could not be read this time (not "none").
  documents_failed?: boolean;
};
export type Addressees = { to: string[]; cc: string[]; rep_missing: boolean };
// `kept`: named from the plan, without its link, on a sheet whose entry the feed no longer shows.
export type SupplierDocument = { id: number; title: string | null; is_image: boolean; uploaded_at: string | null; url: string | null; mine: boolean; kept?: boolean };
export type MaybeItem = {
  name: string | null; qty: number; product_type_slug: string | null; sku: string | null; image_url: string | null;
  product_url: string | null; order_id: number; supplier: string | null; reference: string | null; arrival_on: string | null; arrived: boolean;
  // Not his case: ordered for this client, but arrived before the feed shows the client's deliveries.
  unseen?: boolean;
};
export type Delivery = {
  event: Record<string, unknown>;
  // Which list this is, and whether ticks land in it (the job's run is ticked in one list only).
  list: FurnitureList; editable: boolean;
  orders?: CitedOrder[]; cited_order_ids?: number[]; unreadable_order_ids?: number[];
  // The list's own sum of minutes per piece.
  estimated_minutes: number;
  // His `a.est`, the job's ONE estimate (Run.estimate_of; the run's copy once the entry is gone): the
  // head, the timer, the gauge, the finished line and the Finish dialog read only this.
  estimate_minutes?: number | null;
  // His `rcptBox`: who an issue report goes to, and who in copy (the report's own rule, server side).
  addressees?: Addressees;
  // Myotaku's admin site, for his links.
  admin_url?: string;
  // After a start or "+ Fitter": who was taken off which other job (his toast), and that job's status
  // after the move — so its screen drops them at once, as his `claim` does.
  released?: { names: string[]; client: string | null; event_id?: number; status?: string }[];
  // Items checked here that the Delivery Manager does not show yet — to tick there by hand.
  delivery_manager_to_tick: number;
  totals: { packs: number; lines: number; pieces: number };
  run: Run | null;
  issues: Issue[];
  packs: Pack[];
  // His "à vérifier" (step 2a bis): ordered under a name close to the client's, not attached for sure.
  maybe?: MaybeItem[]; maybe_since?: string | null;
  // A write's answer: the job's card figures, worked out fresh on its own list — the card and the sheet
  // head take them with the answer, never one read later.
  progress?: JobProgress | null;
  // The job as the list carries it (AssemblyJobs#one): a sheet opened on a job no list holds takes its
  // delivery day and archive state from it.
  job?: AssemblyJob | null;
  // A finish's or resend's answer: what became of THIS tap's summary (his finGo's own sendMail) —
  // queued for Slack, failed to be, or not sent by the fitter's choice — whatever another tablet wrote since.
  recap_outcome?: "queued" | "failed" | "none";
};


// Ludovic's Montage list: his assembly jobs only ("Assemblage meubles pour …"), with his four tabs.
export type JobTab = "today" | "upcoming" | "done" | "archived";
export type AssemblyJob = {
  id: number; number: number | null; assembly: boolean; title: string | null;
  client: string | null; contract_id: number | null; contract_name: string | null;
  day: string; starts_at: string | null; delivery_on: string | null;
  estimate_text: string | null; estimate_minutes: number | null;
  warehouse: { id: number; name: string } | null; type_label: string | null;
  // A job kept from its run alone has no planning entry to say it was approved (null).
  approved: boolean | null; sales_approved: boolean | null; logistics_approved: boolean | null;
  // The contract's owner, his "Sales rep".
  owner?: string | null;
  // His card: the team, the person-time so far, and while it runs how many people the clock counts
  // (as of `as_of`), so the time keeps ticking on the screen; the run's own copy of the estimate, for a
  // job the list has none for.
  run: { id: number; status: string; number: number | null; finished_at: string | null; furniture_list: FurnitureList;
         team?: string[]; worked_minutes?: number; worked_seconds?: number; as_of?: string; running_people?: number;
         estimated_minutes?: number | null } | null;
  tab: JobTab;
  // `archived` is his `isArch`, never true while the job runs; `held`, where it goes once it stops (his
  // `arcOver`, else `arcAuto`) — the same as `archived` for a stopped job, the choice a pause files a
  // running one by. A list that does not send `held` says it for a stopped job only.
  archive: ArchiveState & { held?: ArchiveState };
  // Problems reported on it; `gone` = its planning entry was removed (listed from its run).
  issues: number; gone: boolean;
  // His card numbers worked out ahead (the same object /progress answers), so the card paints with
  // them; null while the server has not worked them out yet.
  progress?: JobProgress | null;
};
export type ArchiveState = { archived: boolean; how: "manual" | "auto" | null; by: string | null; at: string | null };
export type JobsResponse = {
  today: string; warehouses: { id: number; name: string }[]; jobs: AssemblyJob[]; preparations: ClientPreparation[];
};
// His fitters' guide video in the screen's language (French, else English), as signed links.
export type GuideVideo = { language: "fr" | "en"; video_url: string; poster_url: string };
export const fetchGuide = (lang: string) => call<GuideVideo>(`/api/v1/preparation/guide?lang=${encodeURIComponent(lang)}`);
// The whole planning; `assemblyOnly` asks the server for Ludovic's Montage list only.
export const fetchJobs = (assemblyOnly = true) =>
  call<JobsResponse>(`/api/v1/preparation/jobs${assemblyOnly ? "?assembly_only=true" : ""}`);
// His card numbers for the jobs on screen, refreshed after the list (null while the server has not
// worked them out: the next read brings them).
export type JobProgress = {
  assembled: { done: number; total: number }; checked: { done: number; total: number };
  not_arrived: number; received: { got: number; total: number }; work_pct: number;
  reception: "warn" | "ok" | "confirmed" | null;
  // The job's one estimate, the same number its head shows (his `a.est`).
  estimate_minutes?: number | null;
  // The run status the numbers were worked out for (finished forces the 100 %).
  status?: string | null;
};
export const fetchProgress = (ids: number[]) =>
  call<{ progress: Record<string, JobProgress | null> }>(`/api/v1/preparation/progress?ids=${ids.join(",")}`);
export const archiveJob = (id: number, archived: boolean) =>
  post<AssemblyJob>(`/api/v1/preparation/events/${id}/archive`, { archived });

export const fetchDays = (date?: string) =>
  call<DaysResponse>(`/api/v1/preparation/days${date ? `?date=${date}` : ""}`);
// `fromOrders`: true is Ludovic's list, false the whole Delivery Manager list, null the job's own (the
// one its card counts: his for an assembly entry not started yet).
export const fetchDelivery = (id: number, fromOrders: boolean | null = null) =>
  call<Delivery>(`/api/v1/preparation/events/${id}${fromOrders == null ? "?own=true" : `?from_orders=${fromOrders}`}`);

// His job photos before the start: the first one makes the job's run (idle, in the list shown; unsaid —
// a tablet's photo — the job's own list, as the server opens a job not started).
export const mediaRun = (id: number, fromOrders?: boolean) =>
  post<{ run_id: number }>(`/api/v1/preparation/events/${id}/media_run`, { from_orders: fromOrders });
// The run made for a first file whose row was never written: taken back while it holds nothing.
export const withdrawMediaRun = (id: number, runId: number) =>
  call<{ removed_run: number | null }>(`/api/v1/preparation/events/${id}/media_run?run_id=${runId}`, { method: "DELETE" });
export const startRun = (id: number, fitters: string[], fromOrders = false) =>
  post<Delivery>(`/api/v1/preparation/events/${id}/run`, { fitters, from_orders: fromOrders });
// His "Reprendre": the server restarts with the team stored on the run (422 when nobody is left on it).
export const resumeRun = (id: number) => post<Delivery>(`/api/v1/preparation/events/${id}/run`, { resume: true });
export const pauseRun = (id: number) => post<Delivery>(`/api/v1/preparation/events/${id}/run/pause`);
// Also on a job not started: the first "+ Fitter" makes its run, in the list shown.
export const addFitters = (id: number, fitters: string[], fromOrders?: boolean) =>
  post<Delivery>(`/api/v1/preparation/events/${id}/run/fitters`, { fitters, from_orders: fromOrders });
export const removeFitter = (id: number, name: string) =>
  post<Delivery>(`/api/v1/preparation/events/${id}/run/fitters/remove`, { name });
// A tap is a delta (`*_delta`: +1, −1), applied by the server to the count as it is now, so a stale
// screen never takes a piece back; "tick all" sends the count itself.
export type QtyChange = { assembled?: number; checked?: number; assembled_delta?: number; checked_delta?: number };
export const setQuantity = (id: number, itemId: number, body: QtyChange & { line_ids?: number[] }) =>
  call<Delivery>(`/api/v1/preparation/events/${id}/items/${itemId}`, {
    method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  });
export const reportIssue = (id: number, body: {
  kind: string; description: string; item_id?: number | null; line_id?: number | null; affected_qty?: number | null;
  reported_by: string[]; blocks_assembly: boolean; from_orders?: boolean;
}) => post<Delivery>(`/api/v1/preparation/events/${id}/issues`, body);
// `estimated_minutes`: the estimate the dialog showed, stored as it is (his finGo does not recompute it).
export const finishRun = (id: number, body: { packing_confirmed: boolean; recap_notes?: string; send_recap: boolean; tick_all_assembled?: boolean; tick_all_checked?: boolean; estimated_minutes?: number | null }) =>
  post<Delivery>(`/api/v1/preparation/events/${id}/finish`, body);
// His resend sheet: the summary again, with the note as edited there — "" clears it (his d.note=noteTxt||null).
// `send: false`: his Finish sheet after a failed send, its send tick off — the note only.
export const sendRecap = (id: number, recapNotes?: string, send = true) =>
  post<Delivery>(`/api/v1/preparation/events/${id}/recap`, { ...(recapNotes === undefined ? {} : { recap_notes: recapNotes }), ...(send ? {} : { send_recap: false }) });
// His manager's "↺ Reset this assembly" and its undo.
export const resetRun = (id: number) => post<Delivery>(`/api/v1/preparation/events/${id}/run/reset`);
export const undoResetRun = (id: number) => post<Delivery>(`/api/v1/preparation/events/${id}/run/reset/undo`);
export const reopenRun = (id: number) => post<Delivery>(`/api/v1/preparation/events/${id}/reopen`);
// His picker: the warehouse's planning staff as extras and fixes, the names added by hand there, and
// who is busy on which client.
export type Fitters = {
  extras: string[]; fixes: string[]; added: string[]; busy_on: Record<string, string>;
  known: string[]; busy: string[]; crew: { id: number; first_name: string; permanent: boolean }[];
};
// `eventId`: the job the picker is for, left out of "on <client>".
export const fetchFitters = (warehouseId?: number | null, eventId?: number | null) => {
  const q = new URLSearchParams();
  if (warehouseId) q.set("warehouse_id", String(warehouseId));
  if (eventId) q.set("event_id", String(eventId));
  return call<Fitters>(`/api/v1/preparation/fitters${q.size ? `?${q}` : ""}`);
};
// His "Ajouter un prénom": kept for that warehouse.
export const addFitterName = (warehouseId: number, name: string) =>
  post<Fitters>("/api/v1/preparation/fitters", { warehouse_id: warehouseId, name });

export type Alert = { kind: "yield" | "idle"; value: number; limit: number };
export type Productivity = {
  wall_minutes: number; worked_minutes: number; estimated_minutes: number;
  // The worked time exact, in person-seconds (rounded once on screen).
  worked_seconds?: number;
  earned_minutes: number; credited_minutes: number;
  // null until there is something to divide: the screen shows a dash, never a fake 0 %.
  yield_ratio: number | null;
  idle_minutes: number; idle_limit_minutes: number; team_size: number;
  pieces_total: number; pieces_done: number; judgeable: boolean; alerts: Alert[];
  // His `P.ok`: false when the job's sheet has no item, so there is nothing to measure.
  measurable: boolean;
};
export type SupervisorJob = {
  run: { id: number; number: number | null; status: string; started_at: string | null };
  event: {
    id: number; number: number | null; client_name: string | null;
    starts_at: string | null; warehouse: { id: number; name: string } | null;
  };
  // The job's own planning estimate, the one its job page measures against (his `a.est`).
  estimate_minutes: number | null;
  team: string[];
  productivity: Productivity;
  // When today's alert of each kind went out (null: at the next hourly check); anything validated yet.
  alerts_sent?: { yield: string | null; idle: string | null };
  validated?: boolean;
  largest_outstanding: { name: string; minutes_per_piece: number; pieces: number } | null;
  // His sparkline: 49 even steps from the start to now, [person-minutes spent, minutes earned].
  spark?: [number, number][] | null;
};
export type SupervisorBoard = {
  jobs: SupervisorJob[];
  // His four figures: people = distinct fitters on running jobs; alerts = every alert on the live jobs.
  totals: { jobs: number; running: number; people: number; alerts: number };
};

export const fetchSupervisor = (warehouseId: number | null) =>
  call<SupervisorBoard>(`/api/v1/preparation/supervisor${warehouseId ? `?warehouse_id=${warehouseId}` : ""}`);

// His "Journée minute par minute": today's blocks and validated pieces per fitter, jobs in colour order.
export type DayTimeline = {
  from: string; now: string;
  jobs: { event_id: number; client: string | null; number: number | null }[];
  rows: {
    name: string;
    blocks: { event_id: number; from: string; to: string }[];
    ticks: { event_id: number; at: string; piece: string | null }[];
  }[];
};

export const fetchDayTimeline = (warehouseId: number | null) =>
  call<DayTimeline>(`/api/v1/preparation/day_timeline${warehouseId ? `?warehouse_id=${warehouseId}` : ""}`);

// His `fitStats`: per fitter over today / 7 days / 30 days. `fitters` is empty unless the reader is an
// admin; the totals give his "today's rate".
export type FitterPeriod = "today" | "7d" | "30d";
export type FitterStat = { name: string; minutes: number; earned: number; items: number; alerts: number; rate: number | null };
export type FitterStats = { from: string; fitters: FitterStat[]; total: { minutes: number; earned: number; rate: number | null } };

export const fetchFitterStats = (warehouseId: number | null, period: FitterPeriod) =>
  call<FitterStats>(`/api/v1/preparation/fitter_stats?period=${period}${warehouseId ? `&warehouse_id=${warehouseId}` : ""}`);

// His `renderStats`: every finished job with an estimate, complete or not — listed and summed alike.
export type HistoryJob = {
  // `number` is the planning entry's number (his "#"), null when the run never had one.
  run_id: number; number: number | null; event_id: number;
  client_name: string | null; warehouse_name: string | null;
  finished_at: string | null; planned_on: string | null;
  estimated_minutes: number; worked_minutes: number; wall_minutes: number;
  // Over 1 means the job beat its estimate; null when no time was worked.
  ratio: number | null;
  // His "Écart": (actual − estimate) / estimate, on every job.
  gap: number;
  team: string[]; minutes_by_person: Record<string, number>;
  packing_confirmed: boolean;
};
export type History = {
  jobs: HistoryJob[];
  summary: {
    jobs: number; estimated_minutes: number; worked_minutes: number; ratio: number | null;
    gap: number | null; within_estimate: number;
  };
};

export const fetchHistory = (warehouseId: number | null) =>
  call<History>(`/api/v1/preparation/history${warehouseId ? `?warehouse_id=${warehouseId}` : ""}`);
